import { useCallback } from 'react';
import { trpc } from '@org/clients/school';
import { FEATURE_FLAG_DEFAULTS, type FeatureFlagKey, type FeatureFlags, type UserType } from '@org/model';
import { useAuth } from '../components/login/auth-context';

type FlagProfile = {
  type?: UserType | string | null;
  signupSource?: string | null;
  featureFlags?: FeatureFlags | null;
} | null | undefined;

/** Flags a self-signed-up student always has: their whole experience IS the journey and the store. */
const SELF_SIGNUP_FLAGS: ReadonlySet<FeatureFlagKey> = new Set<FeatureFlagKey>(['journey', 'journey-products']);

/**
 * Pure flag resolution, shared by the hook and its tests.
 *
 * - The user's row (`users.feature_flags`) wins when it has an entry for the key.
 * - With no entry, `FEATURE_FLAG_DEFAULTS` decides.
 * - b2b students never get the journey/mural experience, whatever the row says.
 * - Students who signed up by themselves (`signup_source = 'self'`) always
 *   have the journey and the store: with both off the access gate would send
 *   them to the store and the store's flag guard back to the dashboard, forever.
 */
export const resolveFeatureFlag = (flag: FeatureFlagKey, profile: FlagProfile): boolean => {
  if (flag === 'journey' && profile?.type === 'b2b') return false;
  if (profile?.signupSource === 'self' && SELF_SIGNUP_FLAGS.has(flag)) return true;
  const stored = profile?.featureFlags?.[flag];
  if (typeof stored === 'boolean') return stored;
  return FEATURE_FLAG_DEFAULTS[flag];
};

/**
 * Per-user feature flags.
 *
 * A flag is enabled when the user's DB row (`users.feature_flags`) turns it
 * on, or — when the row has no entry for that key — when its default in
 * `FEATURE_FLAG_DEFAULTS` (`@org/model`) is on. There is no admin/permission
 * override, so admins (and the E2E auth bypass user, which holds `admin:all`)
 * see exactly what their own flags allow. Flags are managed per user from the
 * admin app's student form.
 *
 * Reuses the same `users.getUserByEmail` query as `LocaleProvider`, so it adds
 * no extra network requests.
 */
export const useFeatureFlags = () => {
  const { user } = useAuth();
  const email = user?.email ?? '';
  const { data: profile, isLoading } =
    trpc.users.getUserByEmail.useQuery(email, { enabled: Boolean(email) });

  const isEnabled = useCallback(
    (flag: FeatureFlagKey): boolean => resolveFeatureFlag(flag, profile),
    [profile?.featureFlags, profile?.type],
  );

  return { isEnabled, isLoading };
};

export const useFeatureFlag = (flag: FeatureFlagKey) => {
  const { isEnabled, isLoading } = useFeatureFlags();
  return { isEnabled: isEnabled(flag), isLoading };
};
